import { useCallback, useEffect } from 'react';
import { RoutingRuleType } from 'src/graphql';
import {
  currentStateRuleIsSaved,
  StateRule,
} from '../helpers/compareAndFormatRoutingRules';
import { useFetchRoutingRules } from '../../../pages/Routing/hooks/useFetchRoutingRules';

type SaveRuleFunctionType = () => void;

export const useCheckUnsavedRulesOnLeave = (
  currStateRule: StateRule,
  leavingRoutingPage: boolean,
  saveCurrentRule: SaveRuleFunctionType,
  saveRulesBeforeLeaving: boolean,
  onLeavingRoutingPageCallback?: (
    checkUnsavedRulesCallback: () => boolean,
  ) => void,
) => {
  const { orderedRules: savedRules } = useFetchRoutingRules();

  const unsavedRulesExist = useCallback(
    (currStateRule: StateRule) => {
      const savedRule = (savedRules.find(
        (savedRule) => savedRule.id === currStateRule.id,
      ) ?? {}) as RoutingRuleType;
      const currentRuleIsUnsaved = Boolean(
        !currentStateRuleIsSaved(savedRule, currStateRule),
      );
      return currentRuleIsUnsaved;
    },
    [savedRules],
  );

  useEffect(() => {
    if (leavingRoutingPage && onLeavingRoutingPageCallback) {
      onLeavingRoutingPageCallback(() => unsavedRulesExist(currStateRule));
    }
  }, [
    currStateRule,
    leavingRoutingPage,
    onLeavingRoutingPageCallback,
    unsavedRulesExist,
  ]);

  useEffect(() => {
    const isUnsavedRule = unsavedRulesExist(currStateRule);
    if (saveRulesBeforeLeaving && isUnsavedRule) {
      saveCurrentRule();
      onLeavingRoutingPageCallback?.(() => unsavedRulesExist(currStateRule));
    }
  }, [
    saveRulesBeforeLeaving,
    saveCurrentRule,
    onLeavingRoutingPageCallback,
    currStateRule,
    unsavedRulesExist,
  ]);

  return { unsavedRulesExist, saveCurrentRule };
};
